import { useLocalStorage } from "@vueuse/core"
import { computed } from "vue"
import { ViewMode } from "@/utils"

export type TileSize = "small" | "medium" | "large" | "xlarge"

const STORAGE_KEY_VIEW_MODE = "manage-media-view-mode"
const STORAGE_KEY_TILE_SIZE = "manage-media-tile-size"

function tileSizeToValue(size: TileSize): number {
  const map: Record<TileSize, number> = { small: 0, medium: 1, large: 2, xlarge: 3 }
  return map[size]
}

function valueToTileSize(value: number): TileSize {
  const map: Record<number, TileSize> = { 0: "small", 1: "medium", 2: "large", 3: "xlarge" }
  return map[value] ?? "medium"
}

/**
 * Composable for manage-media page: view mode (table/card) and tile size (card grid).
 * Persists both to localStorage. Returns mode (respecting mobile override), setViewMode, and tile size–related state (grid class, label, slider value).
 */
export function useManageMediaView() {
  const viewModePreference = useLocalStorage<ViewMode>(STORAGE_KEY_VIEW_MODE, ViewMode.CARD)
  const tileSizePreference = useLocalStorage<TileSize>(STORAGE_KEY_TILE_SIZE, "medium")

  const mode = computed<ViewMode>(() => {
    return viewModePreference.value
  })

  function setViewMode(value: ViewMode) {
    viewModePreference.value = value
  }

  const gridColsClass = computed(() => {
    if (tileSizePreference.value === "small") {
      return "grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8"
    }
    if (tileSizePreference.value === "large") {
      return "grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
    }
    if (tileSizePreference.value === "xlarge") {
      return "grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
    }
    return "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"
  })

  const tileSizeLabel = computed(() => {
    const labels: Record<TileSize, string> = {
      small: "Small",
      medium: "Medium",
      large: "Large",
      xlarge: "XL",
    }
    return labels[tileSizePreference.value]
  })

  const tileSizeSliderValue = computed({
    get: () => tileSizeToValue(tileSizePreference.value),
    set: (value: number) => {
      tileSizePreference.value = valueToTileSize(value)
    },
  })

  return {
    mode,
    setViewMode,
    gridColsClass,
    tileSizeLabel,
    tileSizeSliderValue,
  }
}
